import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { MultiSelectDropdown } from './MultiSelectDropdown';
import { Pagination, PaginationProps } from './Pagination';
import { TextInput, NumberRangeInput } from './TextInput';
import { Button } from './buttons/Button';
import { Download } from '../assets/Download';
import { FilterParams } from '../hooks/useFetchWithPagination';
import { Option } from '../pages/types';

export type FilterConfig = {
  key: string;
  label: string;
  type: 'text' | 'multiselect' | 'numberrange';
  options?: Option[]; // For multiselect
  placeholder?: string;
};

type TableToolsProps = {
  children: React.ReactNode;
  forPagination?: PaginationProps;
  renderPagination: boolean;
  isLoading?: boolean;
  searchBy?: (t: string, field: string) => void;
  searchPlaceholder?: string;
  reFetch: () => void;
  hasData: boolean;
  createLabel: string;
  createLink: string;
  onClickCreate?: () => void;
  changelogLink?: string;
  onClickDownload?: () => void;
  filters?: FilterConfig[];
  applyFilters?: (filters: FilterParams, searchText?: string, searchField?: string) => void;
  clearFilters?: () => void;
  currentFilters?: FilterParams;
  currentSearchText?: string;
  currentSearchField?: string;
};

export const TableTools = ({
  renderPagination,
  forPagination,
  searchBy,
  searchPlaceholder,
  reFetch,
  isLoading = false,
  children,
  hasData,
  createLabel,
  createLink,
  changelogLink,
  onClickCreate,
  onClickDownload,
  filters = [],
  applyFilters,
  clearFilters,
  currentFilters = {},
  currentSearchText = '',
  currentSearchField,
}: TableToolsProps) => {
  const [localSearchText, setLocalSearchText] = useState(currentSearchText);
  const [filterValues, setFilterValues] = useState<FilterParams>(currentFilters);

  // Sync local search text with prop when it changes
  useEffect(() => {
    setLocalSearchText(currentSearchText || '');
  }, [currentSearchText]);

  if (isLoading) return null;

  const handleApplyFilters = () => {
    if (applyFilters) {
      applyFilters(filterValues, localSearchText, 'search');
    }
  };

  const handleClearFilters = () => {
    setFilterValues({});
    setLocalSearchText('');
    if (clearFilters) {
      clearFilters();
    }
  };

  const updateFilterValue = (key: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const renderFilter = (filter: FilterConfig) => {
    switch (filter.type) {
      case 'text':
        return (
          <div key={filter.key} className="mr-4 w-48">
            <TextInput
              label={filter.label}
              placeholder={filter.placeholder}
              setTextChange={(value) => updateFilterValue(filter.key, value)}
              value={(filterValues[filter.key] as string) || ''}
            />
          </div>
        );
      case 'multiselect': {
        const selectedOptions = Array.isArray(filterValues[filter.key])
          ? ((filterValues[filter.key] as string[])
              .map((value) => filter.options?.find((opt) => opt.value === value))
              .filter(Boolean) as Option[])
          : [];
        return (
          <div key={filter.key} className="mr-4 w-64">
            <label className="block text-sm font-medium mb-1">{filter.label}</label>
            <MultiSelectDropdown
              options={filter.options || []}
              selectedOptions={selectedOptions}
              setSelectedOptions={(options) =>
                updateFilterValue(
                  filter.key,
                  options.map((opt) => opt.value)
                )
              }
              placeholder={filter.placeholder || `Select ${filter.label.toLowerCase()}`}
            />
          </div>
        );
      }
      case 'numberrange':
        return (
          <div key={filter.key} className="mr-4 w-64">
            <label className="block text-sm font-medium mb-1">{filter.label}</label>
            <NumberRangeInput
              minValue={filterValues[`${filter.key}_min`] as number}
              maxValue={filterValues[`${filter.key}_max`] as number}
              setMinValue={(value) => updateFilterValue(`${filter.key}_min`, value)}
              setMaxValue={(value) => updateFilterValue(`${filter.key}_max`, value)}
              placeholder={filter.placeholder || filter.label}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="pb-[10px]">
        {/* Search Row */}
        {searchBy && (
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center">
              <TextInput
                className="w-[150px] mr-[10px] !text-sm"
                label=""
                placeholder={searchPlaceholder}
                setTextChange={setLocalSearchText}
                value={localSearchText}
              />
              <div className="flex items-center justify-center">
                <Button
                  label="Search"
                  onClick={() => searchBy(localSearchText, 'search')}
                  textClassName="text-sm"
                />
              </div>
            </div>
          </div>
        )}

        {/* Filters Row */}
        {filters.length > 0 && (
          <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
            <div className="flex flex-wrap items-end gap-4">
              {filters.map(renderFilter)}
              <div className="flex gap-2">
                <Button
                  label="Apply Filters"
                  onClick={handleApplyFilters}
                  textClassName="text-sm"
                />
                <Button
                  label="Clear Filters"
                  onClick={handleClearFilters}
                  variant="secondary"
                  textClassName="text-sm"
                />
              </div>
            </div>
            <div className="flex flex-row space-x-4">
              {changelogLink && (
                <div className="flex flex-row items-center">
                  <Link className="cursor-pointer underline text-anchor" to={changelogLink}>
                    View Changelog
                  </Link>
                </div>
              )}
              <Button
                textClassName="text-sm"
                label={createLabel}
                onClick={() => {
                  if (onClickCreate) {
                    onClickCreate();
                  } else {
                    location.href = createLink;
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* Action Buttons Row - Only show if no filters */}
        {!filters.length && (
          <div className="flex justify-between">
            <div />
            <div className="flex flex-row space-x-4">
              {changelogLink && (
                <div className="ml-2 flex flex-row items-center">
                  <Link className="cursor-pointer underline text-anchor" to={changelogLink}>
                    View Changelog
                  </Link>
                </div>
              )}
              {onClickDownload && (
                <div
                  className="flex flex-row items-center space-x-1 cursor-pointer"
                  onClick={onClickDownload}
                >
                  <Download className="w-4 h-4 " />
                  <a href="#" className="font-bold underline">
                    Download
                  </a>
                </div>
              )}
              <Button
                label={createLabel}
                onClick={() => {
                  if (onClickCreate) {
                    onClickCreate();
                  } else {
                    location.href = createLink;
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>
      {hasData ? (
        <>
          {children}
          {renderPagination && forPagination && <Pagination {...forPagination} />}
        </>
      ) : (
        <>
          <div className="text-center items-center justify-center mt-8">
            <span> No results </span>
            <br />
            <Link to="#" onClick={reFetch} className="font-bold underline">
              Go Back
            </Link>
          </div>
        </>
      )}
    </>
  );
};
